import RightIcon from "../../icons/RightIcon";
import { additionalManageItems, businessMenuItems, manageMenuItems, resourcesMenuItems } from './menu-items'
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import "../../public-v33.css";

interface BusinessMenuItem {
  name: string;
  href: string;
  description?: string;
  icon?: string;
}

interface MegaMenuMobileProps {
  onBack: () => void;
  selectedMenu?: string;
}

export default function MegaMenuMobile({ onBack, selectedMenu }: MegaMenuMobileProps) {
  const [items, setItems] = useState<BusinessMenuItem[]>([]);
  const [title, setTitle] = useState<string>('');

  useEffect(() => {
    if (!selectedMenu) return;
    switch (selectedMenu) {
      case 'start-business':
        setItems(businessMenuItems);
        setTitle('Start a Business');
        break;
      case 'manage':
        setItems([...manageMenuItems, ...additionalManageItems])
        setTitle('Manage Your Business');
        break;
      case 'resources':
        setItems(resourcesMenuItems)
        setTitle('Resources');
        break;
      default:
        setItems([]);
        break;
    }
  }, [selectedMenu])

  return (
    <div className="flex flex-col h-full w-full max-w-full md:max-w-[24rem] md:w-96 p-0 bg-white">
      {/* Header */}
      <div className="px-5 pb-4 pt-6 border-b border-white/10 flex items-center justify-between bg-white relative top-0 z-10">
        <div className="flex justify-start items-center gap-0.5 cursor-pointer z-10" onClick={onBack}>
          <RightIcon className="w-6 h-6 -rotate-180" />
          <div className="text-black text-sm font-semibold">Back</div>
        </div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-black text-base font-bold pointer-events-none w-max">
          {title}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Menu Items Container */}
        <div className="w-full mt-[30px] flex flex-col gap-4 px-6">
          {items.map((item) => (
            <div key={item.name} className="self-stretch inline-flex justify-between items-center">
              <div className="flex-1 flex justify-start items-center gap-4">
                {item.icon && (
                  <div className="w-12 h-12 relative">
                    <div className="w-12 h-12 left-0 top-0 absolute bg-slate-100 rounded-3xl" />
                    <div className="absolute left-[10.15px] top-[10.15px]">
                      <Image
                        src={item.icon}
                        alt={`${item.name} icon`}
                        width={28}
                        height={28}
                        className="w-7 h-7"
                      />
                    </div>
                  </div>
                )}
                <div className="v33-sans text-[#101c46] text-base font-semibold leading-7 hover:text-[#a0782a]">
                  <Link href={item.href}>{item.name}</Link>
                </div>
              </div>
              <RightIcon className="w-6 h-6" />
            </div>
          ))}
        </div>

        {/* Bottom Section (Gradient starts below menu items, covers full width) */}
        <section className="relative mt-8 flex flex-col items-center w-full overflow-visible py-8">
          {/* Gradient Background - truly full width */}
          <div className="absolute inset-0 w-full h-full opacity-20 bg-gradient-to-b from-[#22388d] to-transparent shadow-[0px_24px_8px_-20px_rgba(0,15,51,0.05)] border-t border-zinc-200/60 pointer-events-none z-0" />
          <div className="w-full max-w-xl z-10 flex flex-col items-center px-6">
            {/* Title */}
            <h2 className="v33-serif w-full text-center text-[#101c46] text-xl font-semibold leading-9 mb-4">
              {
                selectedMenu === "start-business" ? (
                  <>Start a Business<br />Fast, Smart & Simple</>
                ) : (<>Stay Compliant, Stay<br /> Protected </>)
              }
            </h2>
            {/* Image */}
            <div className="relative flex justify-center w-full">
              <Image
                src={selectedMenu === "start-business" ? "/assets/public/landing/mega-menu-start-a-business.webp" : "/assets/public/landing/mega-menu-manage-a-business.webp"}
                alt="Business people"
                width={273}
                height={250}
                className="w-72 h-64 object-cover"
                priority
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}